import { Link, useParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import BackTop from '@/components/base/BackTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="mx-auto w-full max-w-3xl px-4 py-24 text-center md:px-6">
          <p className="font-seal text-5xl text-primary-600">哎哟</p>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">这篇考据走丢了</h1>
          <p className="mt-3 text-sm text-foreground-600">内容可能已被移动或删除，去看看其他栏目吧。</p>
          <Link
            to="/daily"
            className="mt-6 inline-block whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回内容列表
          </Link>
        </main>
        <Footer />
        <BackTop />
      </div>
    );
  }

  const related = articles.filter((a) => article.relatedIds.includes(a.id)).slice(0, 3);

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: article.title,
      description: article.excerpt,
      datePublished: article.date,
      author: { '@type': 'Person', name: article.author },
      inLanguage: 'zh-CN',
      keywords: article.tags.join(','),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://pxwfrw.cn/' },
        {
          '@type': 'ListItem',
          position: 2,
          name: article.categoryLabel,
          item: `https://pxwfrw.cn/${article.category === 'cp' ? 'cp' : 'daily'}`,
        },
        { '@type': 'ListItem', position: 3, name: article.title },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <Header />
      <main className="mx-auto w-full max-w-4xl px-4 py-10 md:px-6 md:py-14">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: article.categoryLabel, to: article.category === 'cp' ? '/cp' : '/daily' },
            { label: article.title },
          ]}
        />

        <article>
          <header>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-primary-100 px-2.5 py-1 text-xs text-primary-700">{article.categoryLabel}</span>
              {article.tags.map((t) => (
                <span key={t} className="rounded-md bg-secondary-100 px-2 py-1 text-xs text-secondary-900">
                  #{t}
                </span>
              ))}
            </div>
            <h1 className="mt-4 font-heading text-3xl font-black leading-tight tracking-wide text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 text-base leading-8 text-foreground-600 md:text-lg">{article.excerpt}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-background-200/70 pb-6 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" /> {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" /> {article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" /> {article.readTime}阅读
              </span>
            </div>
          </header>

          <div className="relative mt-8 overflow-hidden rounded-xl border border-background-200/70">
            <img
              src={article.cover}
              alt={article.title}
              className="h-64 w-full object-cover object-top md:h-80"
            />
          </div>

          <div className="mt-8 space-y-5">
            {article.content.map((p, i) => (
              <p key={i} className="text-base leading-8 text-foreground-700 md:text-lg md:leading-9">
                {i === 0 ? <strong className="font-heading text-primary-700">「 </strong> : null}
                {p}
                {i === 0 ? <strong className="font-heading text-primary-700"> 」</strong> : null}
              </p>
            ))}
          </div>

          <footer className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-background-200/70 pt-6">
            <p className="font-seal text-lg text-primary-700">愿你也能在师门里找到那份牵挂。</p>
            <div className="flex items-center gap-2">
              <Link
                to={article.category === 'cp' ? '/cp' : '/daily'}
                className="whitespace-nowrap rounded-md border border-primary-300 px-4 py-2 text-sm text-primary-700 transition-colors hover:bg-primary-500 hover:text-background-50"
              >
                返回列表
              </Link>
              <Link
                to="/"
                className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                回到首页
              </Link>
            </div>
          </footer>
        </article>

        {related.length > 0 && (
          <section aria-label="相关推荐" className="mt-16">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关推荐</h2>
            <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((a) => (
                <ArticleCard key={a.id} article={a} compact />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <BackTop />
    </div>
  );
}